export const DASHBOARD_TABS = [
  { key: "overall", label: "Overall" },
  { key: "partners-info", label: "Partners Info" },
  { key: "summary", label: "Summary" },
  { key: "performance", label: "Performance" },
];

export const BANNER_TITLES = {
  overall: "Dashboard & Overview",
  "partners-info": "Dashboard & Overview",
  summary: "Performance Metrics",
  performance: "Performance Metrics",
};

export const MONTH_OPTIONS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

const currentYear = new Date().getFullYear();

export const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => {
  const year = currentYear - i;
  return { label: String(year), value: year };
});

/**
 * Overall tab KPI tiles (left to right).
 * Orders (App Upload) sums rider-submitted order counts on approved deposits.
 * Balance is derived live from Collected − Collected Rider, never stored on its own.
 * Expenses has no data source defined yet and stays static until one is specified.
 */
export const OVERALL_KPI_DEFS = [
  // Deposit figures may be negative (CF-S23), so their totals can be too.
  { key: "ordersRider", label: "Orders (App Upload)", unit: null, allowNegative: true },
  { key: "totalOrders", label: "Total Order Count", unit: null },
  { key: "collectedAmount", label: "Collected Amount", unit: "OMR" },
  { key: "collectedRiderAmount", label: "Collected Rider Amount", unit: "OMR", allowNegative: true },
  { key: "balance", label: "Balance", unit: "OMR", allowNegative: true },
  { key: "expenses", label: "Expenses", unit: "OMR", placeholder: true },
];

/** Sentinel for the "All" entry on the Project / Fleet Type / City filters. */
export const ALL_VALUE = "all";

/** Overall tab renders for exactly one year + one month. */
export function defaultOverallFilters() {
  const now = new Date();
  return {
    project: ALL_VALUE,
    fleetType: ALL_VALUE,
    city: ALL_VALUE,
    zones: [],
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  };
}

export const ORDERS_TREND_SERIES = [
  { key: "ordersCount", label: "Orders Count", color: "#10B981" },
  { key: "partnersCount", label: "Partners Count", color: "#3B82F6", axis: "right" },
];

export const FINANCIAL_TREND_SERIES = [
  { key: "collected", label: "Collected", color: "#3B82F6" },
  { key: "deposited", label: "Deposited", color: "#10B981" },
  { key: "balance", label: "Balance", color: "#EF4444", axis: "right" },
];

/**
 * Summary tab KPI tiles — 12 in a 4-column grid.
 * Variance and Balance are derived live; Variance uses the rider order count
 * behind Orders (App Upload), which now lives on the Overall tab.
 * The two `caveat` tiles sum cashdeposits.ordersCount, which older deposits
 * only got at approval time, so they can under-report on pending/rejected rows.
 */
const RIDER_ORDERS_CAVEAT =
  "Older pending and rejected deposits, submitted before riders entered an order count, can be missing it. This tile may under-report until those are backfilled.";

export const SUMMARY_KPI_DEFS = [
  { key: "ordersUpload", label: "Orders (Admin Upload)", unit: null },
  { key: "collectedUpload", label: "Collected Amount (Admin Upload)", unit: "OMR" },
  // Deposit-summing tiles allow negatives — deposit figures may be negative (CF-S23).
  { key: "riderAmountCollected", label: "Collected Amount (Rider Collected)", unit: "OMR", allowNegative: true },
  // Rider's Amount Deposited — the reconciled figure Balance is measured against.
  { key: "collectedRider", label: "Collected Amount (Rider Deposited)", unit: "OMR", allowNegative: true },
  { key: "varianceOrders", label: "Variance (Order Count)", unit: null, allowNegative: true },
  { key: "balanceAmount", label: "Balance (Amount)", unit: "OMR", allowNegative: true },
  { key: "underApprovalOrders", label: "Under Approval (Order Count)", unit: null, allowNegative: true, caveat: RIDER_ORDERS_CAVEAT },
  { key: "underApprovalAmount", label: "Under Approval (Amount)", unit: "OMR", allowNegative: true },
  { key: "rejectionOrders", label: "Rejection (Order Count)", unit: null, allowNegative: true, caveat: RIDER_ORDERS_CAVEAT },
  { key: "leavesPending", label: "Leaves Pending", unit: null },
  { key: "leavesApproved", label: "Leaves Approved", unit: null },
  { key: "leavesRejected", label: "Leaves Rejected", unit: null },
];

/** Summary lands on the current month, everything else unfiltered. */
export function defaultSummaryFilters() {
  const now = new Date();
  return {
    project: ALL_VALUE,
    fleetType: ALL_VALUE,
    city: ALL_VALUE,
    zones: [],
    rider: ALL_VALUE,
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  };
}

/**
 * Performance tab KPI tiles.
 * Fuel and login hours have no data source yet and stay static.
 */
export const PERFORMANCE_KPI_DEFS = [
  { key: "completedOrders", label: "Completed Orders", unit: null },
  { key: "noShowPercent", label: "No Show %", unit: "%" },
  { key: "totalFuelConsumed", label: "Total Fuel Consumed", unit: null, placeholder: true },
  { key: "totalHoursLogin", label: "Total Hours Login", unit: null, placeholder: true },
];

/** Performance lands on the current month, everything else unfiltered. */
export function defaultPerformanceFilters() {
  const now = new Date();
  return {
    project: ALL_VALUE,
    fleetType: ALL_VALUE,
    city: ALL_VALUE,
    zones: [],
    rider: ALL_VALUE,
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    fromDate: null,
    toDate: null,
  };
}

// formatKpiValue moved to components/common/KPICard when the card was promoted
// to a shared component; re-exported here so existing importers keep working.
export { formatKpiValue } from "../../components/common/KPICard";
